import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { MessageSquareWarning } from "lucide-react";
import { Optional } from "@/lib/utils";
import { SpecList } from "@/types/specList";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import SpecsSelectTable from "@/components/shared/SpecsSelectTable";
import { MaskWithStructures } from "@/types/mask";
import { useMemo } from "react";
import { Loader } from "@/components/ui/loader";

type SpecTableWrapperProps = {
  maskWithStructures: Optional<MaskWithStructures> | null; // Is optional so that it doesn't break type coercion
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  rowSelectionState: RowSelectionState;
  specListId: string;
  specLists: SpecList[];
  supportedRecipes: string[];
  isLoading: boolean;
};
export function SpecsTableWrapper({
  maskWithStructures,
  specListId,
  specLists,
  onRowSelectionChange,
  rowSelectionState,
  supportedRecipes,
  isLoading,
}: SpecTableWrapperProps) {
  const specList = useMemo(() => {
    if (specListId) {
      return specLists.find((sl) => sl.id === specListId);
    }

    return null;
  }, [specListId, specLists]);

  const specData = useMemo(() => {
    const structureNames =
      maskWithStructures?.structures?.filter((s) => !s.isCustom).map((s) => s.name) ?? [];

    if (specList) {
      const filteredSpecs = specList.latestVersion.specValues.filter(
        (specValue) =>
          structureNames.includes(specValue.spec.name) ||
          structureNames.includes(specValue.spec.reportName) ||
          structureNames.includes(specValue.spec.dutNames) ||
          structureNames.includes(specValue.spec.legacyNames),
      );

      const specsFilteredOnRecipe = filteredSpecs.filter(
        (s) => s.spec.recipe && supportedRecipes.includes(s.spec.recipe.id),
      );

      return specsFilteredOnRecipe.map((s) => s.spec).flat();
    }

    return [];
  }, [maskWithStructures?.structures, specList, supportedRecipes]);

  if (isLoading) {
    return <Loader text="Loading specs..." />;
  }

  if (specData.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <MessageSquareWarning />
          </EmptyMedia>
          <EmptyTitle>No results.</EmptyTitle>
          <EmptyDescription>Try selecting a different measurement capability.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <SpecsSelectTable
      onRowSelectionChange={onRowSelectionChange}
      rowSelectionState={rowSelectionState}
      specData={specData}
    />
  );
}
